import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  Facture,
  FactureInput,
  FactureListQuery,
  FactureUpdate,
} from '@sirajoptic/shared';
import { api } from '@/lib/api-client';
import { cleDashboard } from '@/features/dashboard/api/dashboard.api';

export const clesFactures = {
  toutes: ['factures'] as const,
  liste: (q: Partial<FactureListQuery>) => ['factures', 'liste', q] as const,
  une: (id: string) => ['factures', id] as const,
};

export function useFactures(q: Partial<FactureListQuery>) {
  return useQuery({
    queryKey: clesFactures.liste(q),
    queryFn: () => api.liste<Facture>('/factures', q),
    placeholderData: (precedent) => precedent,
  });
}

export function useFacture(id: string | undefined) {
  return useQuery({
    queryKey: clesFactures.une(id ?? ''),
    queryFn: () => api.get<Facture>(`/factures/${id}`),
    enabled: Boolean(id),
  });
}

/** Émettre ou annuler touche au stock : le tableau de bord suit. */
function useInvalider() {
  const client = useQueryClient();
  return () => {
    void client.invalidateQueries({ queryKey: clesFactures.toutes });
    void client.invalidateQueries({ queryKey: ['produits'] });
    void client.invalidateQueries({ queryKey: ['mouvements'] });
    void client.invalidateQueries({ queryKey: ['commandes'] });
    void client.invalidateQueries({ queryKey: ['clients'] });
    void client.invalidateQueries({ queryKey: cleDashboard });
  };
}

export function useCreerFacture() {
  const invalider = useInvalider();
  return useMutation({
    mutationFn: (input: FactureInput) => api.post<Facture>('/factures', input),
    onSuccess: invalider,
  });
}

export function useModifierFacture() {
  const invalider = useInvalider();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: FactureUpdate }) =>
      api.patch<Facture>(`/factures/${id}`, patch),
    onSuccess: invalider,
  });
}

export function useSupprimerFacture() {
  const invalider = useInvalider();
  return useMutation({
    mutationFn: (id: string) => api.delete<{ supprime: boolean }>(`/factures/${id}`),
    onSuccess: invalider,
  });
}

/** Règle 3 — sortie de stock, totaux figés, montant en lettres. */
export function useEmettreFacture() {
  const invalider = useInvalider();
  return useMutation({
    mutationFn: (id: string) => api.post<Facture>(`/factures/${id}/emettre`),
    onSuccess: invalider,
  });
}

/** Règle 2 — une facture émise s'annule, elle ne se supprime pas. */
export function useAnnulerFacture() {
  const invalider = useInvalider();
  return useMutation({
    mutationFn: ({ id, motif }: { id: string; motif: string }) =>
      api.post<Facture>(`/factures/${id}/annuler`, { motif }),
    onSuccess: invalider,
  });
}
